import { useLingui } from "@lingui/react/macro";
import { List, Table, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";

import { DocHeading } from "@/components/docs/DocHeading.tsx";
import { DocsContent } from "@/components/docs/DocsContent.tsx";

export const Route = createFileRoute("/docs/property-search/costs")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useLingui();

  // Price per property in SEK: [internal Geodatasamverkan, internal other, external Geodatasamverkan, external other]
  const prices: Array<[string, number, number, number, number]> = [
    [t`General section`, 10, 10, 0, 15],
    [t`Designations`, 5, 5, 0, 10],
    [t`Addresses`, 5, 5, 0, 10],
    [t`Owner & Mortgages`, 15, 15, 0, 20],
    [t({ message: `Land regulation`, context: `docs` }), 15, 15, 0, 20],
    [t`Rights`, 15, 15, 0, 20],
    [t`Taxation`, 20, 20, 0, 25],
  ];

  const formatPrice = (price: number) =>
    i18n.number(price, {
      style: "currency",
      currency: "SEK",
      maximumFractionDigits: 0,
    });

  return (
    <DocsContent>
      <Title order={1} size="h2">{t`Cost overview`}</Title>
      <Text>
        {t`REALity is intended to provide access to official data at a controlled and transparent cost. The intention is
        that the cost of data is allocated to the project that uses it.`}
      </Text>

      <DocHeading
        order={2}
        size="h3"
      >{t`What affects the cost of a request?`}</DocHeading>
      <List withPadding>
        <List.Item>{t`The type of search`}</List.Item>
        <List.Item>{t`The selected datasets`}</List.Item>
        <List.Item>{t`The number of properties`}</List.Item>
        <List.Item>{t`Whether the customer participates in Geodatasamverkan and is registered in REALity`}</List.Item>
      </List>

      <DocHeading order={2} size="h3">{t`Pricelist for datasets`}</DocHeading>
      <Text>
        {t`The prices below apply per property for each selected dataset. 'Other' represents the projects
        that are not registered as members of Geodatasamverkan.`}
        {t`External refers to users working at Sweco, and internal refers to users working for another organization.`}
      </Text>
      <Table maw="900px">
        <Table.Thead>
          <Table.Tr>
            <Table.Th rowSpan={2}>{t`Dataset`}</Table.Th>
            <Table.Th colSpan={2}>{t`Internal`}</Table.Th>
            <Table.Th colSpan={2}>{t`External`}</Table.Th>
          </Table.Tr>
          <Table.Tr>
            <Table.Th>{t`Geodatasamverkan`}</Table.Th>
            <Table.Th>{t`Other`}</Table.Th>
            <Table.Th>{t`Geodatasamverkan`}</Table.Th>
            <Table.Th>{t`Other`}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {prices.map(([dataset, ...columns]) => (
            <Table.Tr key={dataset}>
              <Table.Td>{dataset}</Table.Td>
              {columns.map((price, index) => (
                <Table.Td key={index}>{formatPrice(price)}</Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <DocHeading order={2} size="h3">{t`Geodatasamverkan`}</DocHeading>
      <Text>
        {t`Government agencies, regions, municipalities and organisations performing public functions can
        enter into an agreement and thereby gain access to geospatial data and services for public use. The
        Geodatasamverkan product offering includes geospatial data and services from, among others,
        Lantmäteriet.`}
      </Text>
      <Text>
        {t`If the customer participates in the Geodatasamverkan, their agreement can be linked to the
        REALity assignment, so that they are not charged for free datasets. However, administrative costs for
        REALity may still apply. Contact the REALity support team via the menu in the upper-right corner to
        access data under the customer’s Geodatasamverkan agreement. Specify which customer the request
        concerns, and you will then be contacted.`}
      </Text>

      <DocHeading order={2} size="h3">{t`External use`}</DocHeading>
      <Text>{t`REALity may also be offered externally:`}</Text>
      <List withPadding>
        <List.Item>{t`As a hosted software service, or SaaS.`}</List.Item>
        <List.Item>{t`As an on-premises installation.`}</List.Item>
        <List.Item>{t`As part of a broader customer solution.`}</List.Item>
        <List.Item>{t`As a platform for integrations and applications.`}</List.Item>
      </List>
      <Text>
        {t`For external customers, pricing may include a service margin.`}
      </Text>
    </DocsContent>
  );
}
